import { Injectable } from "@angular/core";
import { InvoiceType } from "../models/Dte/invoiceType.enum";
import { Permiso } from "../models/Perfil/Permiso";
import { Permission } from "../shared/models/Permission";
import { TokenStorageService } from "./token-storage.service";

@Injectable({
	providedIn: "root",
})
export class PermissionService {
	private PERMISSIONS_ROLE = "permission-list";
	private ROUTE_PERMISSIONS = "route-permissions";
	private currentPermissions: Permission[] = [];
	private routePermissions: Map<string, boolean> = new Map();
	private basePermission: Permission = undefined;
	permissionContainer = window.sessionStorage;
	public isSuperAdmin = this.tokenService.getUser().superAdmin;
	public isOffline: boolean = false;

	constructor(private tokenService: TokenStorageService) {
		if (this.currentPermissions.length == 0) {
			this.getPermissionList();
		}
	}

	savePermissionList(list: Permission[]) {
		this.currentPermissions = list || [];
		this.permissionContainer.setItem(this.PERMISSIONS_ROLE, JSON.stringify(this.currentPermissions));
		this.loadRoutePermissions();
	}

	getPermissionList() {
		let setList = JSON.parse(this.permissionContainer.getItem(this.PERMISSIONS_ROLE)) as Permission[];
		this.currentPermissions = setList;
		this.loadRoutePermissions();
	}

	getPermission(code: string) {
		let permission = this.currentPermissions ? this.currentPermissions.find((perm) => perm.codigo?.trim() == code) : undefined;
		if (!permission) {
			permission = new Permission();
			permission.codigo = code;
			permission.asignado = false;
		}
		return permission;
	}

	canIssueInvoice(type: InvoiceType): boolean {
		const codes = {
			[InvoiceType.Factura]: "40-18-10",
			[InvoiceType.GuiaDespacho]: "40-18-10",
			[InvoiceType.Boleta]: "40-18-33",
			[InvoiceType.FacturaCompra]: "40-18-34",
			[InvoiceType.FacturaExportacion]: "40-19-10",
			[InvoiceType.LiquidacionFactura]: "40-21-10",
			[InvoiceType.NotaDebito]: "40-18-31",
			[InvoiceType.NotaCredito]: "40-18-30",
		};
		return this.getPermission("40").asignado && !!codes[type] && this.getPermission(codes[type]).asignado;
	}

	getAccessPermission() {
		if (this.currentPermissions) {
			let perm = this.currentPermissions.find((perm) => perm.codigo === "10");
			if (perm) {
				this.basePermission = perm;
				return this.basePermission;
			} else {
				let dummy = new Permiso();
				dummy.asignado = true;
				return dummy;
			}
		} else {
			let dummy = new Permiso();
			dummy.asignado = true;
			return dummy;
		}
	}

	signOut(): void {
		this.currentPermissions = [];
		this.permissionContainer.removeItem(this.PERMISSIONS_ROLE);
		this.routePermissions.clear();
		this.permissionContainer.removeItem(this.ROUTE_PERMISSIONS);
	}

	loadRoutePermissions() {
		//load the Map of the relationships between route strings and permissions

		this.routePermissions.set("/inicio", true);

		this.routePermissions.set("/ventas/factura-masiva", this.getPermission("40") && this.getPermission("40-10") ? this.getPermission("40").asignado && this.getPermission("40-10").asignado : false); //only own permission
		this.routePermissions.set("/ventas/documentos-emitidos", this.getPermission("40") && this.getPermission("40-18") ? this.getPermission("40").asignado && this.getPermission("40-18").asignado : false);
		this.routePermissions.set("/ventas/exportaciones", this.getPermission("40") && this.getPermission("40-19") ? this.getPermission("40").asignado && this.getPermission("40-19").asignado : false);
		this.routePermissions.set("/ventas/boletas-honorarios-emitidas", this.getPermission("80") ? this.getPermission("80").asignado : false);
		this.routePermissions.set("/ventas/boletas-honorarios-masiva", this.getPermission("80").asignado);
		this.routePermissions.set("/ventas/guias-despacho", this.getPermission("40") && this.getPermission("40-20") ? this.getPermission("40").asignado && this.getPermission("40-20").asignado : false);
		this.routePermissions.set("/ventas/cesiones", !this.isOffline && this.getPermission("45") && this.getPermission("45-10") ? this.getPermission("45").asignado && this.getPermission("45-10").asignado : false);
		
		this.routePermissions.set("/compras/boletas-honorarios-recibidas", this.getPermission("81") ? this.getPermission("81").asignado : false);
		this.routePermissions.set("/compras/documentos-recibidos", this.getPermission("50") && this.getPermission("50-10") ? this.getPermission("50").asignado && this.getPermission("50-10").asignado : false);
		this.routePermissions.set("/compras/facturas-compras", this.getPermission("40") && this.getPermission("40-18") ? this.getPermission("40").asignado && this.getPermission("40-18").asignado : false);
		
		this.routePermissions.set("/mantenedores/proveedores", this.getPermission("30") ? this.getPermission("30-30").asignado : false); ////check parent Permission "Mantenedores" and own permission "PRoveedores"		
		this.routePermissions.set("/mantenedores/folios", this.getPermission("20-10") ? this.getPermission("20-10").asignado : false); //if dont get ConfiguracionDte permission, folios only should be 20-10
		this.routePermissions.set("/mantenedores/clientes", this.getPermission("30") ? this.getPermission("30-20").asignado : false); ////check parent Permission "Mantenedores" and own permission "Clientes"
		this.routePermissions.set("/mantenedores/productos", this.getPermission("30") ? this.getPermission("30-60").asignado : false);
		this.routePermissions.set("/mantenedores/servicios", this.getPermission("30-60").asignado);
		this.routePermissions.set("/mantenedores/usuarios", this.getPermission("30") ? this.getPermission("30-40").asignado : false);		
		this.routePermissions.set("/mantenedores/cesionarios", !this.isOffline && this.getPermission("30") && this.getPermission("30-15") ? this.getPermission("30").asignado && this.getPermission("30-15").asignado : false);
		this.routePermissions.set("/mantenedores/clientes-internacionales", this.getPermission("30") && this.getPermission("30-25") ? this.getPermission("30").asignado && this.getPermission("30-25").asignado : false);
		
		this.routePermissions.set("/configuracion/certificados", this.getPermission("20-20") ? this.getPermission("20-20").asignado : false); //if dont get ConfiguracionDte permission, Certificados only 20-20		
		this.routePermissions.set("/configuracion/alertas", this.getPermission("30") ? this.getPermission("30-110").asignado : false);
		this.routePermissions.set("/configuracion/empresas", this.getPermission("30") && this.getPermission("30-80") ? this.getPermission("30").asignado && this.getPermission("30-80").asignado : false);
		this.routePermissions.set("/configuracion/integraciones", this.getPermission("86").asignado);
		this.routePermissions.set("/configuracion/perfiles", this.getPermission("30") && this.getPermission("30-50") ? this.getPermission("30").asignado && this.getPermission("30-50").asignado : false);
		this.routePermissions.set("/configuracion/perfiles/permisos", this.getPermission("30-50-14").asignado);
		// Payku: por ahora se controla con el permiso base de administración (30) o superadmin
		this.routePermissions.set("/configuracion/payku", this.isSuperAdmin || (this.getPermission("83") ? this.getPermission("83").asignado : false));
		this.routePermissions.set("/configuracion/mis-plantillas", this.isSuperAdmin || (this.getPermission("85") ? this.getPermission("85").asignado : false));
		
		this.routePermissions.set("/consolidado/ventas", this.getPermission("90").asignado);
		
		

		this.routePermissions.set("/cuenta", true);
		this.routePermissions.set("/mi-cuenta", true);
		this.routePermissions.set("/plan", true);
		this.routePermissions.set("/plan-facturacion", true);
		this.routePermissions.set("/emisor-boletas-honorarios", this.getPermission("80") && this.getPermission("80-16") ? this.getPermission("80").asignado && this.getPermission("80-16").asignado : false);
		this.routePermissions.set("/facturador-individual", Object.values(InvoiceType).some(type => typeof type === "number" && this.canIssueInvoice(type)));
		this.routePermissions.set("/crear-emisor", this.getPermission("50") && this.getPermission("30-80-22") ? this.getPermission("30").asignado && this.getPermission("30-80-22").asignado : false);
		this.routePermissions.set("/crear-emisor", this.getPermission("30") && this.getPermission("30-80-22") ? this.getPermission("30").asignado && this.getPermission("30-80-22").asignado : false);
		this.routePermissions.set("/empresa", this.getPermission("30") && this.getPermission("30-80-26") ? this.getPermission("30").asignado && this.getPermission("30-80-26").asignado : false);
		
		if (this.routePermissions.size > 0) {
			this.saveRoutePermissionList();
		}
	}

	saveRoutePermissionList() {
		//save the loaded routePermissions into storage: session | local , for persistency of the Map if page is reloaded preventing the logout of the app
		this.permissionContainer.removeItem(this.ROUTE_PERMISSIONS);
		this.permissionContainer.setItem(this.ROUTE_PERMISSIONS, JSON.stringify(this.routePermissions));
	}

	getRoutePermission(routeName: string): boolean {
		this.checkSuperAdmin();
		const normalizedRouteName = this.normalizeRouteName(routeName);
		if (!this.routePermissions.has(normalizedRouteName)) {
			this.getPermissionList();
		}
		let permission = this.routePermissions.has(normalizedRouteName) ? this.routePermissions.get(normalizedRouteName) : false;
		return permission;
	}

	private normalizeRouteName(routeName: string): string {
		if (!routeName) {
			return routeName;
		}

		return routeName.split("?")[0].split("#")[0];
	}

	fakePermission(perm: Permission) {
		let code = perm.codigo.split("-");
		return code[0] == "10" || code[0] == "20";
	}

	checkSuperAdmin() {
		if (this.isSuperAdmin == undefined) {
			this.isSuperAdmin = this.tokenService.getUser().superAdmin;
		}
	}
}
